<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.unit") }}</label>
        <DxSelectBox
          v-model="localData.department_uuid"
          :data-source="getDepartmentList()"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onDepartmentChanged"
          :class="{
            'field-error': this.$v.localData.department_uuid?.$error,
          }"
          placeholder=""
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.invoice_management.invoice_date") }}</label>
        <date-picker2
          v-model="localData.date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          :class="{ 'field-error': this.$v.localData.date.$error }"
          @change="onDateChanged"
          ref="datePicker"
        ></date-picker2>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.invoice_management.invoice_number") }}</label>
        <DxAutocomplete
          v-model="localData.invoice_number"
          :data-source="[]"
          :min-search-length="0"
          :show-clear-button="true"
          :class="{
            'field-error': this.$v.localData.invoice_number?.$error,
          }"
        />
      </div>
      <div class="form-group col-sm-4 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.partner") }}</label>
        <div
          class="sm-select-input-dialog-btn"
          @click="partnerClick"
          :class="{
            'field-error': this.$v.localData.partner_uuid?.$error,
          }"
        >
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ getPartnerNames }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.invoice_management.due_date") }}</label>
        <date-picker2
          v-model="localData.due_date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          @change="onDueDateChanged"
          :class="{
            'field-error': this.$v.localData.due_date?.$error,
          }"
          ref="datePicker"
        ></date-picker2>
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.invoice_management.payment_date") }}</label>
        <date-picker2
          v-model="localData.payment_date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          @change="onPaymentDateChanged"
          ref="datePicker"
        ></date-picker2>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.products.vat") }}</label>
        <DxSelectBox
          v-model="localData.vat_code"
          :data-source="getVatList"
          display-expr="name"
          value-expr="id"
          :class="{
            'field-error': this.$v.localData.vat_code?.$error,
          }"
          :on-value-changed="onVatChanged"
          placeholder=""
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.invoice_management.discount") }} %</label>
        <input v-model.number="localData.discount" type="number" min="0" max="100" step="0.1" class="form-control" placeholder="0.00" />
      </div>
    </div>
    <PartnerSelector
      ref="partnerSelector"
      :visible="showPartnerDialog"
      @close="showPartnerDialog = false"
      @continue="onContinueFromPartnerSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxScrolling } from "devextreme-vue/data-grid";
import { DxRadioGroup } from "devextreme-vue/radio-group";
import DatePicker2 from "vue2-datepicker";
import required from "vuelidate/src/validators/required";
import moment from "moment";
import DxAutocomplete from "devextreme-vue/autocomplete";
import PartnerSelector from "@/components/PartnerSelector.vue";

export default {
  name: "settings",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.formData }, // Create a local copy of formData
      showPartnerDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      departments: "company/departments",
      smProductVatList: "smProducts/smProductVatList",
    }),
    getPartnerNames() {
      if (!this.localData.partner_name) {
        return " - ";
      }
      return this.localData.partner_name;
    },
    getVatList() {
      const vatList = Array.isArray(this.smProductVatList) ? this.smProductVatList : [];
      if (vatList.length === 0) {
        return [{ id: null, name: "-" }];
      }
      return [{ id: null, name: "-" }, ...vatList];
    },
  },
  components: {
    DxSelectBox,
    DxRadioGroup,
    DatePicker2,
    DxScrolling,
    DxAutocomplete,
    PartnerSelector,
  },
  methods: {
    ...mapActions({
      getDepartments: "company/getDepartments",
      getSmProductVatList: "smProducts/getSmProductVatList",
    }),
    onDateChanged(date) {},
    onPaymentDateChanged(date) {},
    onDueDateChanged(date) {},
    onPartnerChanged(e) {
      if (e.value && this.partners.length > 0) {
        const selectedPartner = this.partners.find((partner) => partner.uuid === e.value);
        if (selectedPartner) {
          this.localData.partner_name = selectedPartner.name;
        }
      }
    },
    onDepartmentChanged(e) {
      if (e.value && this.departments.length > 0) {
        const selected = this.departments.find((dept) => dept.uuid === e.value);
        if (selected) {
          this.localData.department_name = selected.name;
        }
      }
    },
    onVatChanged(e) {
      /*if (e.value && this.smProductVatList.length > 0) {
        const selectedVat = this.smProductVatList.find((vat) => vat.uuid === e.value);
        if (selectedVat) {
          this.localData.vat_code = selectedVat.name;
        }
      }*/
    },
    getDepartmentList() {
      return this.departments.map((item) => ({
        uuid: item.uuid,
        name: item.name,
      }));
    },
    onContinueFromPartnerSelector(selectedPartners) {
      this.localData.partner_uuid = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].uuid : null;
      this.localData.partner_name = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].name : null;
      this.showPartnerDialog = false;
    },
    partnerClick() {
      this.showPartnerDialog = true; // Show the PartnerSelector dialog
    },
    validate() {
      this.$v.$touch(); // Trigger validation
      if (this.$v.$invalid) {
        return [this.$t("fill_out_error")];
      }
      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        this.localData = { ...newData }; // Update localData with new data
      }
    },
  },
  mounted() {
    // Set default date format if not already set
    if (!this.localData.uuid && !this.localData.date) {
      this.localData.date = moment().format(this.getDefaultDateFormat);
    }

    this.getDepartments({ limit: 1000 });

    this.getSmProductVatList();
  },
  watch: {
    formData: {
      deep: true,
      handler(newData) {
        if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
          this.localData = { ...newData };
        }
      },
    },
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("input", newData);
        this.$emit("update-form-data", newData, 1); // Emit updated data to parent
      },
    },
  },
  validations() {
    return {
      localData: {
        date: {
          required,
        },
        due_date: {
          required,
        },
        partner_uuid: {
          required,
        },
        department_uuid: {
          required,
        },
        invoice_number: {
          required,
        },
        vat_code: {
          required,
        },
      },
    };
  },
};
</script>
<style scoped></style>
